/* eslint-disable class-methods-use-this */
import { FormState, PageConfig } from "../models/pageConfig"
import StructUtils from "../utils/structUtils"
import ValidatorsUtils from "../utils/validators"
import { Service } from "./Service"
import UIService from "./UIService"
import ActionService from "./action"
import ComponentService from "./component"
import DynamicParamsService from "./dynamicParams"
import StoreService from "./store"

class FormService extends Service {
    formName: string
    formConfig: any
    setState: any
    state: any
    stateRef: any
    initialValues: any
    fieldsState: any
    props: any
    constructor(
        name: string,
        formConfig: any,
        setState: any,
        state: any,
        stateRef: any,
        initialValues: any,
        fieldsState: any,
        props: any
    ) {
        super()
        // set le nom du service
        this.name = "FormService"
        // ajoute toute les methodes du service au tableau de mapping
        this.serviceMap = []
        this.formName = name
        this.formConfig = formConfig
        this.setState = setState
        this.initialValues = initialValues
        this.stateRef = stateRef
        this.fieldsState = fieldsState
        this.props = props
        this.state = state
    }

    /**
     * utilise le CwComponent pour créer
     * l'objet contenant les valeurs par défaut
     */
    public init(): any {
        this.stateRef.current = {
            ...this.stateRef.current,
            isLoading: true,
            edit: this.formConfig?.mode !== "show",
        }
        // lors de l'init on affiche le loader
        this.setState(this.stateRef.current)
        // on vient mettre la valeur du componentConfig dans la ref du initialValues
        this.initialValues.current = this.formConfig?.initialValues
        // si les initialValues sont des array il s'agit d'une list d'Action
        if (
            this.initialValues.current &&
            Array.isArray(this.initialValues.current)
        ) {
            ActionService.exec(this.initialValues.current as any[]).then(
                (result) => {
                    this.initialValues.current = result || {}
                    this.stateRef.current = {
                        ...this.stateRef.current,
                        isLoading: false,
                        edit: this.formConfig?.mode !== "show",
                    }
                    this.setState(this.stateRef.current)
                },
                (err) => {
                    console.log(err)
                }
            )
        } else if (this.formConfig?.initialValues) {
            // [key1, key2]
            // [value1, value2]
            const initialValuesArray = Object.values(
                this.formConfig?.initialValues
            )
            const initialValuesKeys = Object.keys(
                this.formConfig?.initialValues
            )

            DynamicParamsService.resolve(initialValuesArray).then(
                (resolvedValuesArray) => {
                    if (resolvedValuesArray && resolvedValuesArray.length > 0) {
                        const resolvedInitialValues: any = {}

                        for (
                            let index = 0;
                            index < initialValuesArray.length;
                            index += 1
                        ) {
                            const element = resolvedValuesArray[index]
                            resolvedInitialValues[initialValuesKeys[index]] =
                                element
                        }
                        this.initialValues.current = resolvedInitialValues
                        this.stateRef.current = {
                            ...this.stateRef.current,
                            isLoading: false,
                            edit: this.formConfig?.mode !== "show",
                        }
                        this.setState(this.stateRef.current)
                    }
                },
                () => {
                    UIService.getInstance().showErrorToast(
                        "Impossible de récupérer ces données.",
                        "CwForm"
                    )
                    this.stateRef.current = {
                        ...this.stateRef.current,
                        isLoading: false,
                    }
                    this.setState(this.stateRef.current)
                }
            )
        } else {
            this.initialValues.current = {}
            this.stateRef.current = {
                ...this.stateRef.current,
                isLoading: false,
            }
            this.setState(this.stateRef.current)
        }
    }
    /**
     * handleClick gére les clics sur editIcon
     */
    public handleEdit = () => {
        this.stateRef.current = {
            ...this.stateRef.current,
            edit: true,
        }
        this.setState(this.stateRef.current)

        if (this.props.onEditChange) this.props.onEditChange(true)
    }

    /**
     * handleClick gére les clics sur showIcon
     */
    handleShow = () => {
        this.stateRef.current = {
            ...this.stateRef.current,
            edit: false,
        }
        this.setState(this.stateRef.current)
        if (this.props.onEditChange) this.props.onEditChange(false)
    }

    handleReset = () => {
        this.initialValues.current = {}
        this.stateRef.current = {
            ...this.stateRef.current,
            isValid:
                this.formConfig?.buttonConfig?.enabledSubmitButton || false,
            errors: [],
            edit: true,
            isLoading: false,
        }
        this.setState(this.stateRef.current)
    }
    /**
     * injectFields création de tous les champs du formulaire et liens des
     * objects de validation et les events (onChange, onBlur ...)
     * avec les instances des champs
     */
    injectFields = () => {
        const componentToInject: Array<React.JSX.Element> = []
        const fieldStateObject: any = {}
        if (this.props.fields?.length) {
            this.props.fields.forEach((component: PageConfig<any>) => {
                const componentParams: PageConfig<any> = component
                if (!componentParams.config) componentParams.config = {}
                const currentFieldName = component.config.fieldName

                // attribut une clef (index) unique au composant
                componentParams.key = component.name
                // attribut un state au champ courant
                fieldStateObject[component.name] = {
                    error: false,
                    touched: false,
                }
                // lien vers le nom du form parent
                componentParams.config.formLink = this.formName

                // on applique le param error si le champ a une erreur
                componentParams.config.error =
                    this.stateRef.current.errors.find(
                        (e: any) => e === currentFieldName
                    ) !== undefined

                // on set la value avec l'initialValue

                componentParams.config.value =
                    currentFieldName &&
                    typeof this.initialValues.current === "object"
                        ? this.initialValues.current[currentFieldName]
                        : undefined
                componentParams.config.edit = this.stateRef.current.edit

                const currentComponents =
                    ComponentService.getInstance().create(componentParams)

                componentToInject.push(currentComponents)
            })

            // set l'objet des etats des champs
            this.fieldsState.current = fieldStateObject
        }
        return componentToInject
    }
    /**
     * Fonction de gestion des maj du model du formulaire
     * @param key nom du champ maj
     * @param value nouvelle valeur
     */
    handleStateChange = (key: string, value: any): FormState => {
        const newState: FormState = {
            touched: true,
            error: false,
        }

        const validationSchema = this.formConfig?.validationSchema
            ? this.formConfig.validationSchema
            : {}
        // validation du champ
        if (validationSchema[key]) {
            const error = ValidatorsUtils.runValidation(
                key,
                value,
                validationSchema[key]
            )
            let newErrors = this.stateRef.current.errors
            // si error
            if (error) {
                // création de l'objet envoyé au composant fils (status)
                newState.error = true
                newState.msg = typeof error === "string" ? error : undefined
                // ajout nouveau champ avec l'erreur
                newErrors.push(key)
                // élimination des doublons dans la liste des erreurs
                newErrors = StructUtils.deleteArrayDouble(
                    this.stateRef.current.errors
                ) as Array<string>
                // update state
                this.stateRef.current = {
                    ...this.stateRef.current,
                    isValid: false,
                    errors: newErrors,
                }
                this.setState(this.stateRef.current)
            } else if (!this.stateRef.current.isValid) {
                // il y a pas d'erreur et que le form n'est pas valide on peut
                // potentiellement passer le form a isValid = true
                newErrors = this.stateRef.current.errors.filter(
                    (v: any) => v !== key
                )
                // aj errors
                this.stateRef.current = {
                    ...this.stateRef.current,
                    isValid: newErrors.length === 0,
                    errors: newErrors,
                }
                this.setState(this.stateRef.current)
                if (this.props?.config?.copy === "session")
                    StoreService.getInstance().local.setSession(key, value)
            }
        } else {
            this.stateRef.current = {
                ...this.stateRef.current,
                isValid: true,
                errors: [],
            }

            this.setState(this.stateRef.current)
            if (this.props?.config?.copy === "session")
                StoreService.getInstance().local.setSession(key, value)
        }

        return newState
    }
    /**
     * Validation des tous les champs avant une soumission du formulaire
     */
    onSubmitValidation = () => {
        const errorMappingTable: Array<string> = []
        this.props.fields?.forEach((field: PageConfig<any>) => {
            const fieldName = field.config?.fieldName
            if (fieldName) {
                const formState = this.handleStateChange(
                    fieldName,
                    StoreService.getInstance().local.get(fieldName)
                )
                if (formState.error) errorMappingTable.push(fieldName)
            }
        })

        return errorMappingTable
    }
    /**
     * submit fait le submit de form
     */
    submit = async (e: any, initialValues: any, setBlockForm?: any) => {
        e.preventDefault()
        if (setBlockForm && typeof setBlockForm === "function")
            setBlockForm(true)
        // récupère les actions à faire lors du submit
        const handleSubmit = this.formConfig?.handleSubmit
            ? this.formConfig.handleSubmit
            : []

        // verification des champs avant la soumission
        const errorMappingTable = this.onSubmitValidation()
        // on bloque le submit si on a des erreurs dans le form
        if (!errorMappingTable.length) {
            // executer les actions
            try {
                await ActionService.exec(handleSubmit, initialValues)
                // on verifie si on doit faire une action apres le submit
                if (this.formConfig?.afterSubmit === "show") this.handleShow()
                if (this.formConfig?.afterSubmit === "reset") this.handleReset()
                if (this.props.onSubmit) this.props.onSubmit()
            } finally {
                if (setBlockForm && typeof setBlockForm === "function")
                    setBlockForm(false)
            }
        } else {
            // update state si des erreurs sont trouvées
            this.stateRef.current = {
                ...this.state, // à vérifier avec state
                isValid: false,
                errors: errorMappingTable,
            }
            this.setState(this.stateRef.current)
            if (setBlockForm && typeof setBlockForm === "function")
                setBlockForm(false)
        }
    }
    /**
     * Transforme le nom des champs avec leurs displayName
     */
    renderErrorFieldName = () => {
        return this.state.errors.map((fieldName: any) => {
            let errorFieldName = ""
            const foundField = this.props.fields?.find(
                (f: any) => f.config.fieldName === fieldName
            )

            if (foundField) {
                errorFieldName =
                    foundField.config?.label || foundField.config?.fieldName
            }
            return errorFieldName.indexOf("{{#lang") !== -1
                ? DynamicParamsService.resolveLang(errorFieldName)
                : errorFieldName
        })
    }
    //
    renderButtonText = (submitButtonText: string) => {
        return DynamicParamsService.resolveLang(submitButtonText)
    }
    /**
     * Récupère le nom de la class pour les bouttons du form
     */
    getButtonClassName = (): string => {
        return this.formConfig?.buttonConfig?.className
            ? this.formConfig.buttonConfig.className
            : "button"
    }
}

export default FormService
